/**
 * Pure building blocks for Routy's setup tools (`routySetupTools.ts`): turning a
 * partial change into the payload each settings or form endpoint expects, and
 * editing a form's questions without breaking its follow-up wiring.
 *
 * Synced verbatim to the mobile app, so every import must exist at the same
 * path there.
 */

import type { Routemize_DTOs_BookingFormDto } from '../../api/models/Routemize_DTOs_BookingFormDto';
import type { Routemize_DTOs_BookingFormQuestionDto } from '../../api/models/Routemize_DTOs_BookingFormQuestionDto';
import type { Routemize_DTOs_BookingFormSettingDto } from '../../api/models/Routemize_DTOs_BookingFormSettingDto';
import type { Routemize_DTOs_CompanySettingsDTO } from '../../api/models/Routemize_DTOs_CompanySettingsDTO';
import type { Routemize_DTOs_CompanyWorkScheduleDTO } from '../../api/models/Routemize_DTOs_CompanyWorkScheduleDTO';
import type { Routemize_DTOs_CreateBookingFormDto } from '../../api/models/Routemize_DTOs_CreateBookingFormDto';
import type { Routemize_DTOs_CreateBookingFormSettingDto } from '../../api/models/Routemize_DTOs_CreateBookingFormSettingDto';
import type { Routemize_DTOs_CreateFormQuestionDto } from '../../api/models/Routemize_DTOs_CreateFormQuestionDto';
import type { Routemize_DTOs_CreateUpdateCompanySettingsDTO } from '../../api/models/Routemize_DTOs_CreateUpdateCompanySettingsDTO';
import type { Routemize_DTOs_UpdateBookingFormDto } from '../../api/models/Routemize_DTOs_UpdateBookingFormDto';
import type { Routemize_DTOs_UpdateBookingFormSettingDto } from '../../api/models/Routemize_DTOs_UpdateBookingFormSettingDto';
import type { Routemize_DTOs_UpdateBusinessBrandingDTO } from '../../api/models/Routemize_DTOs_UpdateBusinessBrandingDTO';
import type { Routemize_DTOs_UpdateCompanyWorkScheduleDTO } from '../../api/models/Routemize_DTOs_UpdateCompanyWorkScheduleDTO';
import type { Routemize_DTOs_UpdateFormQuestionDto } from '../../api/models/Routemize_DTOs_UpdateFormQuestionDto';
import type { Routemize_DTOs_UserAddressDto } from '../../api/models/Routemize_DTOs_UserAddressDto';
import type { Routemize_ValueObjects_RoutePreferences } from '../../api/models/Routemize_ValueObjects_RoutePreferences';
import type { Routemize_Constants_FieldType } from '../../api/models/Routemize_Constants_FieldType';
import { Routemize_Constants_AddressType } from '../../api/models/Routemize_Constants_AddressType';
import { Routemize_Constants_AfterBookingAction } from '../../api/models/Routemize_Constants_AfterBookingAction';
import { Routemize_Constants_BookingFormStatus } from '../../api/models/Routemize_Constants_BookingFormStatus';
import { Routemize_Constants_EventNature } from '../../api/models/Routemize_Constants_EventNature';
import { Routemize_Constants_ServiceCategory } from '../../api/models/Routemize_Constants_ServiceCategory';
import { Routemize_Constants_TravelDistnaceExceedAction } from '../../api/models/Routemize_Constants_TravelDistnaceExceedAction';
import { Routemize_Constants_UltimateSlotPriority } from '../../api/models/Routemize_Constants_UltimateSlotPriority';
import { System_DayOfWeek } from '../../api/models/System_DayOfWeek';
import { formatPhoneNumber, isPlausiblePhoneNumber } from '../../utils/phoneUtils';
import { detachQuestion, linkAnswerToQuestion, removeQuestion, unlinkAnswer } from '../../utils/questionBranchOps';
import {
  UI_TO_FIELD_TYPE,
  isChoiceUiType,
  parseFieldOptions,
  resolveUiType,
  stringifyFieldOptions,
  type FieldOptionsInput,
  type ParsedFieldOptions,
  type QuestionUiType,
} from '../../utils/questionOptionsJson';
import {
  formatDurationMinutes,
  formatTimeString,
  minutesToTimeString,
  normalizeClockTime,
  timeStringToMinutes,
} from '../../utils/timeUtils';
import { MAX_NAME_LENGTH, isValidEmail, isValidName } from '../../utils/validation';
import type { EffectiveScheduleDay } from '../availability/UserWorkScheduleManager';
import { isRoleAllowedOnPlan, type StarterSeatMember } from '../billing/starterSeats';
import type { PlanTier } from '../billing/featureCatalog';
import { generateFormQuestions, type FormGoal } from '../forms/generateFormQuestions';
import { INDUSTRY_TEMPLATES, getAddOnsForIndustry, type IndustryTemplate } from '../forms/industryQuestionTemplates';
import { SYSTEM_ROLE_NAMES, type SystemRoleName } from '../users/UserRoleConfig';
import type { ConfirmChange, ConfirmOutlineRow } from './routyTools';

// ─────────────────────────────────────────────────────────────────────────────
// Shared
// ─────────────────────────────────────────────────────────────────────────────

/** Comparison key for something a person typed: trimmed, single-spaced, lower case. */
export function labelKey(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Trimmed, single-spaced text; '' for anything that isn't a string. */
export function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
}

function ownEntry<V>(record: Readonly<Record<string, V>>, key: unknown): V | undefined {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

function isWholeNumberInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

const onOff = (value: boolean): string => (value ? 'On' : 'Off');

function preview(text: string | null | undefined, fallback: string): string {
  const value = (text ?? '').trim();
  if (!value) return fallback;
  return value.length > 60 ? `"${value.slice(0, 59)}…"` : `"${value}"`;
}

const clock = (minutes: number): string => formatTimeString(minutesToTimeString(minutes));

const clockOrUnset = (time: string | null | undefined): string => (time ? formatTimeString(time) : 'Not set');

// ─────────────────────────────────────────────────────────────────────────────
// Company settings
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Older backends clear these when a settings PUT leaves them out, so every
 * patch echoes them from the GET. The CAPI token comes back masked, and the
 * backend ignores its own mask on write.
 */
const AD_TRACKING_FIELDS = [
  'defaultMetaPixelId',
  'defaultMetaCapiToken',
  'defaultMetaTestEventCode',
  'defaultGoogleAdsConversionId',
  'defaultGoogleAdsConversionLabel',
  'defaultCustomTrackingScript',
] as const;

type AdTrackingField = (typeof AD_TRACKING_FIELDS)[number];
type CompanySettingsUpdate = Routemize_DTOs_CreateUpdateCompanySettingsDTO;
type RoutePreferences = Routemize_ValueObjects_RoutePreferences;

export type CompanySettingsChanges = Omit<CompanySettingsUpdate, 'routePreferences' | AdTrackingField> & {
  routePreferences?: Partial<RoutePreferences>;
};

/**
 * The PUT body for a partial company-settings change. The backend replaces
 * `routePreferences` wholesale, so the stored values are spread under the change.
 */
export function buildCompanySettingsPatch(
  current: Routemize_DTOs_CompanySettingsDTO,
  changes: CompanySettingsChanges,
): CompanySettingsUpdate {
  const { routePreferences, ...rest } = changes;
  const patch: CompanySettingsUpdate = { ...rest };
  if (routePreferences) {
    patch.routePreferences = { ...current.routePreferences, ...routePreferences };
  }
  for (const field of AD_TRACKING_FIELDS) {
    patch[field] = current[field] ?? null;
  }
  return patch;
}

type CompanyToggleField = {
  [K in keyof CompanySettingsUpdate]-?: NonNullable<CompanySettingsUpdate[K]> extends boolean ? K : never;
}[keyof CompanySettingsUpdate];

type RoutePreferenceField =
  | 'defaultStartingLocation'
  | 'defaultEndingLocation'
  | 'firstAppointmentScheduling'
  | 'lastAppointmentScheduling';

interface Choice {
  value: string;
  label: string;
}

/** One key `update_company_settings` accepts. */
export interface CompanySettingSpec {
  key: string;
  label: string;
  /** JSON schema for the tool's input property. */
  schema: { type: 'boolean' | 'number' | 'string'; enum?: readonly (string | number)[]; description: string };
  current: (settings: Routemize_DTOs_CompanySettingsDTO) => string;
  resolve: (value: unknown) => { patch: CompanySettingsChanges; display: string } | { error: string };
}

const DEFAULT_SERVICE_RADIUS_MILES = 60;
const MAX_SERVICE_RADIUS_MILES = 100;
const ROUNDING_INCREMENTS: readonly number[] = [5, 10, 15, 30];

function toggleSetting(
  key: string,
  field: CompanyToggleField,
  label: string,
  shownWhenUnset: boolean,
  description: string,
): CompanySettingSpec {
  return {
    key,
    label,
    schema: { type: 'boolean', description },
    current: settings => onOff(settings[field] ?? shownWhenUnset),
    resolve: value => {
      if (typeof value !== 'boolean') return { error: `${key} must be true or false.` };
      const patch: CompanySettingsChanges = {};
      patch[field] = value;
      return { patch, display: onOff(value) };
    },
  };
}

function routePreferenceSetting(
  key: string,
  field: RoutePreferenceField,
  label: string,
  choices: Record<string, Choice>,
  unset: string,
  description: string,
): CompanySettingSpec {
  return {
    key,
    label,
    schema: { type: 'string', enum: Object.keys(choices), description },
    current: settings => {
      const stored = settings.routePreferences?.[field] || unset;
      return Object.values(choices).find(choice => choice.value === stored)?.label ?? stored;
    },
    resolve: value => {
      const choice = ownEntry(choices, value);
      if (!choice) return { error: `${key} must be one of: ${Object.keys(choices).join(', ')}.` };
      const routePreferences: Partial<RoutePreferences> = {};
      routePreferences[field] = choice.value;
      return { patch: { routePreferences }, display: choice.label };
    },
  };
}

function messageSetting(
  key: string,
  field: 'submitRequestMessage' | 'denyRequestMessage',
  label: string,
  description: string,
): CompanySettingSpec {
  return {
    key,
    label,
    schema: { type: 'string', description },
    current: settings => preview(settings[field], 'Default message'),
    resolve: value => {
      const text = typeof value === 'string' ? value.trim() : '';
      if (!text) return { error: `${key} needs the message text.` };
      if (text.length > 500) return { error: `${key} must be 500 characters or fewer.` };
      const patch: CompanySettingsChanges = {};
      patch[field] = text;
      return { patch, display: preview(text, '') };
    },
  };
}

const ROUTE_ENDPOINTS: Record<string, Choice> = {
  home: { value: 'home', label: 'Home' },
  office: { value: 'office', label: 'Office' },
};

const OUTSIDE_RADIUS_ACTIONS: Record<string, Choice> = {
  submit_request: { value: Routemize_Constants_TravelDistnaceExceedAction.SUBMIT_REQUEST, label: 'Submit a request' },
  deny: { value: Routemize_Constants_TravelDistnaceExceedAction.DENY_APPOINTMENT, label: 'Deny the appointment' },
};

/** "Shown when unset" defaults match what each settings screen displays for a null value. */
export const COMPANY_SETTING_SPECS: readonly CompanySettingSpec[] = [
  routePreferenceSetting('routeStart', 'defaultStartingLocation', 'Routes start from', ROUTE_ENDPOINTS, 'home',
    "Where each person's day starts: their home address or the company office."),
  routePreferenceSetting('routeEnd', 'defaultEndingLocation', 'Routes end at', ROUTE_ENDPOINTS, 'home',
    "Where each person's day ends."),
  routePreferenceSetting('firstAppointment', 'firstAppointmentScheduling', 'First appointment', {
    drive_within_hours: { value: 'includes_travel_time', label: 'Drive starts at work hours' },
    appointment_at_start: { value: 'starts_at_work_hours', label: 'Appointment starts at work hours' },
  }, 'includes_travel_time',
  'drive_within_hours (recommended): the drive to the first stop starts when work hours begin. ' +
    'appointment_at_start: the first appointment itself can start right when work hours begin.'),
  routePreferenceSetting('lastAppointment', 'lastAppointmentScheduling', 'Last appointment', {
    drive_within_hours: { value: 'includes_return_travel', label: 'Drive home ends at work hours' },
    appointment_at_end: { value: 'ends_at_work_hours', label: 'Appointment ends at work hours' },
  }, 'includes_return_travel',
  'drive_within_hours (recommended): the drive home is done by the end of work hours. ' +
    'appointment_at_end: the last appointment can run right up to the end of work hours.'),
  {
    key: 'serviceRadiusMiles',
    label: 'Service radius',
    schema: {
      type: 'number',
      description: `How far customers can be from the route start, as a whole number of miles from 1 to ${MAX_SERVICE_RADIUS_MILES}.`,
    },
    current: settings => `${settings.maxTravelRadiusMiles ?? DEFAULT_SERVICE_RADIUS_MILES} miles`,
    resolve: value =>
      isWholeNumberInRange(value, 1, MAX_SERVICE_RADIUS_MILES)
        ? { patch: { maxTravelRadiusMiles: value }, display: `${value} miles` }
        : { error: `serviceRadiusMiles must be a whole number from 1 to ${MAX_SERVICE_RADIUS_MILES}.` },
  },
  {
    key: 'outsideRadius',
    label: 'Outside the service radius',
    schema: {
      type: 'string',
      enum: Object.keys(OUTSIDE_RADIUS_ACTIONS),
      description: 'submit_request: the customer can still send a request for review. deny: the booking is declined automatically.',
    },
    current: settings => {
      const stored = settings.travelDistanceExceededAction ?? Routemize_Constants_TravelDistnaceExceedAction.SUBMIT_REQUEST;
      return Object.values(OUTSIDE_RADIUS_ACTIONS).find(choice => choice.value === stored)?.label ?? stored;
    },
    resolve: value => {
      const choice = ownEntry(OUTSIDE_RADIUS_ACTIONS, value);
      if (!choice) return { error: `outsideRadius must be one of: ${Object.keys(OUTSIDE_RADIUS_ACTIONS).join(', ')}.` };
      return {
        patch: { travelDistanceExceededAction: choice.value as Routemize_Constants_TravelDistnaceExceedAction },
        display: choice.label,
      };
    },
  },
  messageSetting('outsideRadiusRequestMessage', 'submitRequestMessage', 'Out-of-area request message',
    'What customers outside the radius see when outsideRadius is submit_request.'),
  messageSetting('outsideRadiusDenyMessage', 'denyRequestMessage', 'Out-of-area decline message',
    'What customers outside the radius see when outsideRadius is deny.'),
  toggleSetting('roundedTimes', 'useRoundedTimes', 'Rounded appointment times', false,
    'Snap suggested start times to a clean grid (9:07 becomes 9:10).'),
  {
    key: 'roundingIncrementMinutes',
    label: 'Rounding increment',
    schema: { type: 'number', enum: ROUNDING_INCREMENTS, description: 'Minutes the rounded start times snap to.' },
    current: settings => `${settings.roundingIncrementMinutes ?? 5} min`,
    resolve: value =>
      typeof value === 'number' && ROUNDING_INCREMENTS.includes(value)
        ? { patch: { roundingIncrementMinutes: value }, display: `${value} min` }
        : { error: `roundingIncrementMinutes must be one of: ${ROUNDING_INCREMENTS.join(', ')}.` },
  },
  toggleSetting('teamEmailNotifications', 'enableEmailNotificationForBookingsUpdate', 'Team email notifications', false,
    'Master switch for the team emails below.'),
  toggleSetting('notifyNewRequest', 'notifyWhenNewRequestIsSubmitted', 'Email: new request submitted', false,
    'Email all admins when a customer submits a request.'),
  toggleSetting('notifyRequestStarted', 'notifyOnContactCaptured', 'Email: request started', false,
    "Email all admins when a customer starts a request but doesn't submit or book."),
  toggleSetting('notifyBooked', 'notifyWhenNewAppointmentIsBooked', 'Email: appointment booked', false,
    'Email admins and the assigned person when an appointment is booked.'),
  toggleSetting('notifyRescheduled', 'notifyWhenAppointmentIsReScheduled', 'Email: appointment rescheduled', false,
    'Email admins and the assigned person when an appointment time changes.'),
  toggleSetting('notifyCompleted', 'notifyWhenAppointmentIsMarkedComplete', 'Email: appointment completed', false,
    'Email admins when an appointment is marked complete.'),
  toggleSetting('notifyCancelled', 'notifyWhenNewAppointmentIsCancels', 'Email: appointment cancelled', false,
    'Email admins and the assigned person when an appointment is cancelled.'),
  toggleSetting('notifyNoShow', 'notifyWhenAppointmentIsMarkedNoShow', 'Email: no-show', false,
    'Email admins when an appointment is marked no-show.'),
  toggleSetting('teamTextNotifications', 'enableSMSNotificationForBookingUpdate', 'Team text notifications', false,
    'Text alerts to the team for critical updates.'),
  toggleSetting('dailyScheduleSummary', 'sendDailySummaryOfScheduledAppointmentsToTheUsers', 'Daily schedule summary', false,
    "Each morning, email every person their own appointments for the day."),
  toggleSetting('dailyIncompleteRequests', 'notifyDailyIncompleteRequests', 'Daily incomplete requests', false,
    'A daily email listing requests nobody has finished handling.'),
  toggleSetting('customerEmailConfirmation', 'customer_SendEmailConfirmation', 'Customer email confirmation', true,
    'Default for new appointments: email the customer a confirmation.'),
  toggleSetting('customerTextConfirmation', 'customer_SendSmsConfirmation', 'Customer text confirmation', false,
    'Default for new appointments: text the customer a confirmation.'),
  toggleSetting('customerEmailReminders', 'customer_SendEmailReminders', 'Customer email reminders', true,
    'Default for new appointments: email the customer reminders.'),
  toggleSetting('customerTextReminders', 'customer_SendSmsReminders', 'Customer text reminders', false,
    'Default for new appointments: text the customer reminders.'),
  toggleSetting('emailReminderWeekBefore', 'customer_SendEmailOneWeekBeforeAppointment', 'Email reminder 1 week before', true,
    'Which email reminders go out when customer email reminders are on.'),
  toggleSetting('emailReminderDayBefore', 'customer_SendEmailOneDayBeforeAppointment', 'Email reminder 1 day before', true,
    'Which email reminders go out when customer email reminders are on.'),
  toggleSetting('emailReminderHourBefore', 'customer_SendEmailOneHourBeforeAppointment', 'Email reminder 1 hour before', true,
    'Which email reminders go out when customer email reminders are on.'),
  toggleSetting('emailReminder30MinBefore', 'customer_SendEmailThirtyMinutesBeforeAppointment', 'Email reminder 30 min before', true,
    'Which email reminders go out when customer email reminders are on.'),
  toggleSetting('textReminderWeekBefore', 'customer_SendSMSOneWeekBeforeAppointment', 'Text reminder 1 week before', true,
    'Which text reminders go out when customer text reminders are on.'),
  toggleSetting('textReminderDayBefore', 'customer_SendSMSOneDayBeforeAppointment', 'Text reminder 1 day before', true,
    'Which text reminders go out when customer text reminders are on.'),
  toggleSetting('textReminderHourBefore', 'customer_SendSMSOneHourBeforeAppointment', 'Text reminder 1 hour before', true,
    'Which text reminders go out when customer text reminders are on.'),
  toggleSetting('textReminder30MinBefore', 'customer_SendSMSThirtyMinutesBeforeAppointment', 'Text reminder 30 min before', true,
    'Which text reminders go out when customer text reminders are on.'),
];

export interface SettingsResolution<T> {
  changes: T;
  rows: ConfirmChange[];
  errors: string[];
}

/**
 * Validate `update_company_settings` input against the spec table. Values that
 * already display the same are still written (some are stored as null), but
 * produce no confirm row.
 */
export function resolveCompanySettings(
  current: Routemize_DTOs_CompanySettingsDTO,
  input: Record<string, unknown>,
): SettingsResolution<CompanySettingsChanges> {
  const changes: CompanySettingsChanges = {};
  const rows: ConfirmChange[] = [];
  const errors: string[] = [];
  for (const spec of COMPANY_SETTING_SPECS) {
    if (input[spec.key] === undefined) continue;
    const result = spec.resolve(input[spec.key]);
    if ('error' in result) {
      errors.push(result.error);
      continue;
    }
    const { routePreferences, ...rest } = result.patch;
    Object.assign(changes, rest);
    if (routePreferences) changes.routePreferences = { ...changes.routePreferences, ...routePreferences };
    const from = spec.current(current);
    if (from !== result.display) rows.push({ label: spec.label, from, to: result.display });
  }
  return { changes, rows, errors };
}

/** Plain-language values for every company setting Routy can change, keyed like the tool input. */
export function describeCompanySettings(current: Routemize_DTOs_CompanySettingsDTO): Record<string, string> {
  return Object.fromEntries(COMPANY_SETTING_SPECS.map(spec => [spec.key, spec.current(current)]));
}

// ─────────────────────────────────────────────────────────────────────────────
// Business profile
// ─────────────────────────────────────────────────────────────────────────────

/** The time zones the business settings screen offers. */
export const SUPPORTED_TIME_ZONES: Readonly<Record<string, string>> = {
  'America/New_York': 'Eastern Time',
  'America/Toronto': 'Eastern Time (Toronto)',
  'America/Chicago': 'Central Time',
  'America/Winnipeg': 'Central Time (Winnipeg)',
  'America/Denver': 'Mountain Time',
  'America/Edmonton': 'Mountain Time (Edmonton)',
  'America/Los_Angeles': 'Pacific Time',
  'America/Vancouver': 'Pacific Time (Vancouver)',
  'America/Halifax': 'Atlantic Time',
  'America/St_Johns': 'Newfoundland Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
};

const COUNTRIES: Readonly<Record<string, string>> = { US: 'United States', CA: 'Canada' };

function normalizeWebsite(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return /^https?:\/\/[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?(\/\S*)?$/i.test(withScheme) ? withScheme : null;
}

function normalizeHexColor(value: string): string | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const hex = match[1].length === 3 ? match[1].split('').map(c => c + c).join('') : match[1];
  return `#${hex.toUpperCase()}`;
}

export interface BusinessProfileChanges {
  /** Name, website, email and colors go through the branding endpoint. */
  branding: Routemize_DTOs_UpdateBusinessBrandingDTO;
  settings: Pick<CompanySettingsUpdate, 'phone' | 'timeZone' | 'country'>;
}

export function resolveBusinessProfile(
  current: Routemize_DTOs_CompanySettingsDTO,
  input: Record<string, unknown>,
): SettingsResolution<BusinessProfileChanges> {
  const changes: BusinessProfileChanges = { branding: {}, settings: {} };
  const rows: ConfirmChange[] = [];
  const errors: string[] = [];
  const row = (label: string, from: string | null | undefined, to: string) => {
    if ((from ?? '') !== to) rows.push({ label, from: from || 'Not set', to });
  };

  if (input.businessName !== undefined) {
    const name = cleanText(input.businessName);
    if (!name || name.length > 100) errors.push('businessName must be 1-100 characters.');
    else {
      changes.branding.businessName = name;
      row('Business name', current.businessName, name);
    }
  }
  if (input.website !== undefined) {
    const website = typeof input.website === 'string' ? normalizeWebsite(input.website) : null;
    if (!website) errors.push('website must be a web address like example.com.');
    else {
      changes.branding.businessWebsite = website;
      row('Website', current.businessWebsite, website);
    }
  }
  if (input.email !== undefined) {
    const email = cleanText(input.email);
    if (!isValidEmail(email)) errors.push('email must be a valid email address.');
    else {
      changes.branding.companyEmail = email;
      row('Business email', current.companyEmail, email);
    }
  }
  if (input.phone !== undefined) {
    const phone = typeof input.phone === 'string' ? input.phone : '';
    if (!isPlausiblePhoneNumber(phone)) errors.push('phone must be a full phone number with area code.');
    else {
      const formatted = formatPhoneNumber(phone);
      changes.settings.phone = formatted;
      row('Business phone', current.phone ? formatPhoneNumber(current.phone) : null, formatted);
    }
  }
  if (input.timeZone !== undefined) {
    const label = ownEntry(SUPPORTED_TIME_ZONES, input.timeZone);
    if (!label) errors.push(`timeZone must be one of: ${Object.keys(SUPPORTED_TIME_ZONES).join(', ')}.`);
    else {
      changes.settings.timeZone = input.timeZone as string;
      row('Time zone', current.timeZone ? SUPPORTED_TIME_ZONES[current.timeZone] ?? current.timeZone : null, label);
    }
  }
  if (input.country !== undefined) {
    const label = ownEntry(COUNTRIES, input.country);
    if (!label) errors.push('country must be US or CA.');
    else {
      changes.settings.country = input.country as string;
      row('Country', COUNTRIES[current.country || 'US'], label);
    }
  }
  const colors = [
    ['primaryColor', 'primaryColor', 'Primary color'],
    ['secondaryColor', 'secondaryColor', 'Secondary color'],
    ['accentColor', 'tertiaryColor', 'Accent color'],
  ] as const;
  for (const [key, field, label] of colors) {
    if (input[key] === undefined) continue;
    const color = typeof input[key] === 'string' ? normalizeHexColor(input[key] as string) : null;
    if (!color) errors.push(`${key} must be a hex color like #1F6FEB.`);
    else {
      changes.branding[field] = color;
      row(label, current[field]?.toUpperCase(), color);
    }
  }
  return { changes, rows, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Company hours
// ─────────────────────────────────────────────────────────────────────────────

/** Monday first, matching the settings screens. */
export const WEEK_DAYS: readonly System_DayOfWeek[] = [
  System_DayOfWeek.MONDAY,
  System_DayOfWeek.TUESDAY,
  System_DayOfWeek.WEDNESDAY,
  System_DayOfWeek.THURSDAY,
  System_DayOfWeek.FRIDAY,
  System_DayOfWeek.SATURDAY,
  System_DayOfWeek.SUNDAY,
];

const WEEKDAYS: readonly System_DayOfWeek[] = WEEK_DAYS.slice(0, 5);

const DAY_ALIASES: Readonly<Record<string, System_DayOfWeek>> = (() => {
  const aliases: Record<string, System_DayOfWeek> = {
    tues: System_DayOfWeek.TUESDAY,
    weds: System_DayOfWeek.WEDNESDAY,
    thur: System_DayOfWeek.THURSDAY,
    thurs: System_DayOfWeek.THURSDAY,
  };
  for (const day of WEEK_DAYS) {
    aliases[day.toLowerCase()] = day;
    aliases[day.slice(0, 3).toLowerCase()] = day;
  }
  return aliases;
})();

export function parseWeekday(value: unknown): System_DayOfWeek | null {
  return typeof value === 'string' ? ownEntry(DAY_ALIASES, value.trim().toLowerCase()) ?? null : null;
}

/** "Mon, Tue, Wed" in week order; "Every day" / "No days" at the extremes. */
export function formatDayList(days: readonly System_DayOfWeek[]): string {
  if (days.length === 0) return 'No days';
  if (days.length === 7) return 'Every day';
  return WEEK_DAYS.filter(day => days.includes(day)).map(day => day.slice(0, 3)).join(', ');
}

type DayHours = Pick<
  Routemize_DTOs_CompanyWorkScheduleDTO,
  'isWorkDay' | 'startTime' | 'endTime' | 'lunchStartTime' | 'lunchEndTime'
>;

export function describeDayHours(day: DayHours | null | undefined): string {
  if (!day?.isWorkDay) return 'Closed';
  const hours = `${formatTimeString(day.startTime)} – ${formatTimeString(day.endTime)}`;
  return day.lunchStartTime && day.lunchEndTime
    ? `${hours}, lunch ${formatTimeString(day.lunchStartTime)} – ${formatTimeString(day.lunchEndTime)}`
    : hours;
}

/** One line per day, Monday first, e.g. "Mon: 8:00 AM – 5:00 PM". */
export function describeWeekSchedule(schedule: readonly Routemize_DTOs_CompanyWorkScheduleDTO[]): string[] {
  return WEEK_DAYS.map(day => `${day.slice(0, 3)}: ${describeDayHours(schedule.find(row => row.dayOfWeek === day))}`);
}

export interface DayHoursChange {
  day: System_DayOfWeek;
  open?: boolean;
  start?: string;
  end?: string;
  /** `null` removes the lunch break. */
  lunchStart?: string | null;
  lunchEnd?: string | null;
}

export interface WeekScheduleMerge {
  /** The whole week, as the update endpoint expects it. */
  schedule: Routemize_DTOs_UpdateCompanyWorkScheduleDTO[];
  rows: ConfirmChange[];
  errors: string[];
}

/**
 * Apply per-day changes to the stored week. A closed day being opened without
 * times takes its stored hours, else the first open day's, else 9–5.
 */
export function mergeWeekSchedule(
  current: readonly Routemize_DTOs_CompanyWorkScheduleDTO[],
  dayChanges: readonly DayHoursChange[],
): WeekScheduleMerge {
  const stored = new Map<System_DayOfWeek, Routemize_DTOs_CompanyWorkScheduleDTO>();
  for (const row of current) {
    if (row.dayOfWeek && !stored.has(row.dayOfWeek)) stored.set(row.dayOfWeek, row);
  }
  const firstOpen = WEEK_DAYS.map(day => stored.get(day)).find(row => row?.isWorkDay);
  const fallbackStart = normalizeClockTime(firstOpen?.startTime) ?? '09:00';
  const fallbackEnd = normalizeClockTime(firstOpen?.endTime) ?? '17:00';

  const errors: string[] = [];
  const rows: ConfirmChange[] = [];
  const changeByDay = new Map<System_DayOfWeek, DayHoursChange>();
  for (const change of dayChanges) {
    if (changeByDay.has(change.day)) errors.push(`${change.day} is listed more than once.`);
    else changeByDay.set(change.day, change);
  }

  const schedule = WEEK_DAYS.map((day, index): Routemize_DTOs_UpdateCompanyWorkScheduleDTO => {
    const row = stored.get(day);
    const base: Routemize_DTOs_UpdateCompanyWorkScheduleDTO = {
      id: row?.id,
      dayOfWeek: day,
      startTime: row?.startTime ?? fallbackStart,
      endTime: row?.endTime ?? fallbackEnd,
      lunchStartTime: row?.lunchStartTime ?? null,
      lunchEndTime: row?.lunchEndTime ?? null,
      isWorkDay: row?.isWorkDay ?? false,
      displayOrder: row?.displayOrder ?? index,
    };
    const change = changeByDay.get(day);
    if (!change) return base;

    const touchesHours = [change.start, change.end, change.lunchStart, change.lunchEnd].some(v => v !== undefined);
    let next: Routemize_DTOs_UpdateCompanyWorkScheduleDTO;
    if (change.open === false) {
      if (touchesHours) {
        errors.push(`${day}: hours can't be set on a day that's being closed.`);
        return base;
      }
      next = { ...base, isWorkDay: false };
    } else {
      const start = change.start !== undefined ? normalizeClockTime(change.start) : normalizeClockTime(base.startTime) ?? fallbackStart;
      const end = change.end !== undefined ? normalizeClockTime(change.end) : normalizeClockTime(base.endTime) ?? fallbackEnd;
      if (!start || !end) {
        errors.push(`${day}: use 24-hour times like "08:00" or "17:30".`);
        return base;
      }
      if (timeStringToMinutes(start)! >= timeStringToMinutes(end)!) {
        errors.push(`${day}: closing time has to be after opening time.`);
        return base;
      }

      let lunchStart = normalizeClockTime(base.lunchStartTime);
      let lunchEnd = normalizeClockTime(base.lunchEndTime);
      if (change.lunchStart === null || change.lunchEnd === null) {
        lunchStart = null;
        lunchEnd = null;
      } else if (change.lunchStart !== undefined || change.lunchEnd !== undefined) {
        lunchStart = change.lunchStart !== undefined ? normalizeClockTime(change.lunchStart) : lunchStart;
        lunchEnd = change.lunchEnd !== undefined ? normalizeClockTime(change.lunchEnd) : lunchEnd;
        if (!lunchStart || !lunchEnd) {
          errors.push(`${day}: give both a lunch start and end as 24-hour times.`);
          return base;
        }
      }
      if (lunchStart && lunchEnd) {
        const [ls, le, s, e] = [lunchStart, lunchEnd, start, end].map(t => timeStringToMinutes(t)!);
        if (ls >= le || ls < s || le > e) {
          errors.push(`${day}: lunch has to fall inside work hours. Move it or remove it.`);
          return base;
        }
      }
      next = {
        ...base,
        isWorkDay: true,
        startTime: start,
        endTime: end,
        lunchStartTime: lunchStart,
        lunchEndTime: lunchEnd,
      };
    }

    const from = describeDayHours(row);
    const to = describeDayHours(next);
    if (from !== to) rows.push({ label: day, from, to });
    return next;
  });

  return { schedule, rows, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Booking forms
// ─────────────────────────────────────────────────────────────────────────────

/** Ids the form editor's color picker uses, with their display names. */
export const EVENT_COLORS: Readonly<Record<string, string>> = {
  lavender: 'Lavender',
  mint: 'Mint',
  peach: 'Peach',
  sky: 'Sky',
  rose: 'Rose',
  lemon: 'Lemon',
  sage: 'Sage',
  coral: 'Coral',
  periwinkle: 'Periwinkle',
  blush: 'Blush',
  seafoam: 'Seafoam',
  apricot: 'Apricot',
  lilac: 'Lilac',
  powder: 'Powder Blue',
  cream: 'Cream',
};

const DEFAULT_EVENT_COLOR = 'lavender';

export function matchEventColor(value: unknown): string | null {
  const key = labelKey(typeof value === 'string' ? value : '');
  if (!key) return null;
  return Object.keys(EVENT_COLORS).find(id => id === key || labelKey(EVENT_COLORS[id]) === key) ?? null;
}

export function matchServiceCategory(value: unknown): Routemize_Constants_ServiceCategory | null {
  const key = labelKey(typeof value === 'string' ? value : '').replace(/[\s_-]/g, '');
  if (!key) return null;
  return Object.values(Routemize_Constants_ServiceCategory).find(category => category.toLowerCase() === key) ?? null;
}

const categoryLabel = (category: string): string => category.replace(/([a-z])([A-Z])/g, '$1 $2');

type FormDayField =
  | 'allowMonday'
  | 'allowTuesday'
  | 'allowWednesday'
  | 'allowThursday'
  | 'allowFriday'
  | 'allowSaturday'
  | 'allowSunday';

const FORM_DAY_FIELDS: Readonly<Record<System_DayOfWeek, FormDayField>> = {
  [System_DayOfWeek.MONDAY]: 'allowMonday',
  [System_DayOfWeek.TUESDAY]: 'allowTuesday',
  [System_DayOfWeek.WEDNESDAY]: 'allowWednesday',
  [System_DayOfWeek.THURSDAY]: 'allowThursday',
  [System_DayOfWeek.FRIDAY]: 'allowFriday',
  [System_DayOfWeek.SATURDAY]: 'allowSaturday',
  [System_DayOfWeek.SUNDAY]: 'allowSunday',
};

export function formBookableDays(
  settings: Partial<Pick<Routemize_DTOs_BookingFormSettingDto, FormDayField>> | null | undefined,
): System_DayOfWeek[] {
  return WEEK_DAYS.filter(day => settings?.[FORM_DAY_FIELDS[day]] === true);
}

/** Loose match of a lower-case hint against a form name ("estimate" finds "Free Estimate"). */
export function formNameMatchesHint(formName: string, hint: string): boolean {
  const name = formName.toLowerCase();
  if (name === hint || name.includes(hint)) return true;
  return hint.split(/\s+/).some((w) => w.length >= 4 && name.includes(w));
}

/**
 * Forms matching what the person called one: the id, else an exact name, else a
 * loose name match. Drafts count; archived forms only match by id.
 */
export function matchBookingForms(
  forms: readonly Routemize_DTOs_BookingFormDto[],
  hint: string,
): Routemize_DTOs_BookingFormDto[] {
  const value = hint.trim();
  if (!value) return [];
  const byId = forms.find(form => form.id === value);
  if (byId) return [byId];
  const key = labelKey(value);
  const open = forms.filter(form => !!form.id && form.status !== Routemize_Constants_BookingFormStatus.ARCHIVED);
  const exact = open.filter(form => labelKey(form.formName) === key);
  return exact.length > 0 ? exact : open.filter(form => formNameMatchesHint(labelKey(form.formName), key));
}

const minutesLabel = (minutes: number | null | undefined): string =>
  minutes ? formatDurationMinutes(minutes) : 'None';

const bookingModeLabel = (allowInstantBooking: boolean | null | undefined): string =>
  allowInstantBooking === false ? 'Request only (team approves)' : 'Instant booking';

const assignmentLabel = (nature: Routemize_Constants_EventNature | null | undefined): string =>
  nature === Routemize_Constants_EventNature.ROUND_ROBIN ? 'Smart Assignment (round robin)' : 'Solo';

/** A form's booking settings in plain language, keyed like `update_booking_form`'s input. */
export function describeFormSettings(
  form: Routemize_DTOs_BookingFormDto,
  settings: Routemize_DTOs_BookingFormSettingDto | null,
): Record<string, unknown> {
  const s: Routemize_DTOs_BookingFormSettingDto = settings ?? {};
  return {
    serviceLabel: s.serviceTypeLabel || null,
    internalNote: form.formDescription || null,
    durationMinutes: s.timeAtLocationMinutes ?? null,
    bufferMinutes: s.defaultPostAppointmentBufferMinutes ?? 0,
    bookableDays: formatDayList(formBookableDays(s)),
    earliestStart: s.firstAppointmentTime ? formatTimeString(s.firstAppointmentTime) : null,
    latestStart: s.lastAppointmentTime ? formatTimeString(s.lastAppointmentTime) : null,
    sameDayBooking: form.allowSameDayBooking ?? true,
    sameDayCutoff: s.sameDayBookingCutoffTime ? formatTimeString(s.sameDayBookingCutoffTime) : null,
    minimumNoticeHours: s.minimumNoticeHours ?? null,
    maxDaysAhead: s.maxSchedulingDaysFromToday ?? null,
    bookingMode: form.allowInstantBooking === false ? 'request' : 'instant',
    assignment: form.eventNature === Routemize_Constants_EventNature.ROUND_ROBIN ? 'round_robin' : 'solo',
    emailConfirmation: s.emailConfirmation ?? null,
    textConfirmation: s.smsConfirmation ?? null,
    emailReminders: s.emailReminder ?? null,
    textReminders: s.smsReminder ?? null,
    color: EVENT_COLORS[s.eventColor || DEFAULT_EVENT_COLOR] ?? s.eventColor,
    showInDirectory: form.showInDirectory ?? false,
    directoryCategory: form.directoryCategory ? categoryLabel(form.directoryCategory) : null,
  };
}

export interface BookingWindowFit {
  first: string;
  last: string;
  errors: string[];
  notes: string[];
}

/**
 * Keep a form's earliest and latest start inside company hours the way the form
 * editor does: no earlier than the earliest opening across the form's days, and
 * late enough only that the visit ends by the latest closing, on a 15-minute
 * grid. Values the person asked for are rejected; values carried over are moved.
 */
export function fitBookingWindow(params: {
  first: string;
  last: string;
  durationMinutes: number;
  days: readonly System_DayOfWeek[];
  companyWeek: readonly Routemize_DTOs_CompanyWorkScheduleDTO[];
  firstIsExplicit: boolean;
  lastIsExplicit: boolean;
}): BookingWindowFit {
  const errors: string[] = [];
  const notes: string[] = [];
  let first = timeStringToMinutes(params.first) ?? 9 * 60;
  let last = timeStringToMinutes(params.last) ?? 16 * 60;

  const openDays = params.days
    .map(day => params.companyWeek.find(row => row.dayOfWeek === day))
    .filter((row): row is Routemize_DTOs_CompanyWorkScheduleDTO => !!row?.isWorkDay);
  const closed = params.days.filter(day => !openDays.some(row => row.dayOfWeek === day));
  if (params.companyWeek.length > 0 && closed.length > 0) {
    notes.push(
      `${formatDayList(closed)} ${closed.length === 1 ? 'is a closed day' : 'are closed days'} in company hours, ` +
        'so customers won\'t see slots there until company hours include them.',
    );
  }

  const opens = openDays.map(row => timeStringToMinutes(row.startTime)).filter((m): m is number => m !== null);
  const closes = openDays.map(row => timeStringToMinutes(row.endTime)).filter((m): m is number => m !== null);
  if (opens.length > 0) {
    const floor = Math.ceil(Math.min(...opens) / 15) * 15;
    if (first < floor) {
      if (params.firstIsExplicit) {
        errors.push(`The company opens at ${clock(floor)} on those days, so the earliest start can't be before ${clock(floor)}.`);
      } else {
        first = floor;
        notes.push(`Earliest start moved to ${clock(floor)} to match company hours.`);
      }
    }
  }
  if (closes.length > 0) {
    const latestClose = Math.max(...closes);
    const ceiling = Math.floor((latestClose - params.durationMinutes) / 15) * 15;
    if (last > ceiling) {
      if (params.lastIsExplicit) {
        errors.push(
          `With ${formatDurationMinutes(params.durationMinutes)} visits and closing at ${clock(latestClose)}, ` +
            `the latest start can be ${clock(ceiling)} at the latest.`,
        );
      } else {
        last = Math.max(ceiling, first + 15);
        notes.push(`Latest start moved to ${clock(last)} so visits end by closing time.`);
      }
    }
  }
  if (first >= last) errors.push('The latest start has to be after the earliest start.');
  return { first: minutesToTimeString(first), last: minutesToTimeString(last), errors, notes };
}

export interface BookingFormChanges {
  detail: Routemize_DTOs_UpdateBookingFormDto;
  settings: Routemize_DTOs_UpdateBookingFormSettingDto;
  /** Booking-window adjustments and warnings worth telling the person about. */
  notes: string[];
}

/** Validate `update_booking_form` input against the stored form; only changed fields come back. */
export function resolveBookingFormChanges(
  form: Routemize_DTOs_BookingFormDto,
  stored: Routemize_DTOs_BookingFormSettingDto | null,
  companyWeek: readonly Routemize_DTOs_CompanyWorkScheduleDTO[],
  input: Record<string, unknown>,
): SettingsResolution<BookingFormChanges> {
  const detail: Routemize_DTOs_UpdateBookingFormDto = {};
  const settings: Routemize_DTOs_UpdateBookingFormSettingDto = {};
  const notes: string[] = [];
  const rows: ConfirmChange[] = [];
  const errors: string[] = [];
  const s: Routemize_DTOs_BookingFormSettingDto = stored ?? {};
  const row = (label: string, from: string, to: string) => {
    if (from !== to) rows.push({ label, from, to });
  };
  const flag = (key: string): boolean | undefined => {
    if (input[key] === undefined) return undefined;
    if (typeof input[key] === 'boolean') return input[key] as boolean;
    errors.push(`${key} must be true or false.`);
    return undefined;
  };
  const time = (key: string): string | undefined => {
    if (input[key] === undefined) return undefined;
    const value = typeof input[key] === 'string' ? normalizeClockTime(input[key] as string) : null;
    if (!value) errors.push(`${key} must be a 24-hour time like "09:00".`);
    return value ?? undefined;
  };

  if (input.name !== undefined) {
    const name = cleanText(input.name);
    if (!name || name.length > 100) errors.push('name must be 1-100 characters.');
    else {
      detail.formName = name;
      row('Name', form.formName ?? '', name);
    }
  }
  if (input.internalNote !== undefined) {
    const note = cleanText(input.internalNote);
    if (!note || note.length > 500) errors.push('internalNote must be 1-500 characters.');
    else {
      detail.formDescription = note;
      row('Internal note', preview(form.formDescription, 'None'), preview(note, ''));
    }
  }
  if (input.serviceLabel !== undefined) {
    const label = cleanText(input.serviceLabel);
    if (!label || label.length > 50) errors.push('serviceLabel must be 1-50 characters.');
    else {
      settings.serviceTypeLabel = label;
      row('What customers book', s.serviceTypeLabel || 'Not set', label);
    }
  }
  if (input.durationMinutes !== undefined) {
    const minutes = input.durationMinutes;
    if (!isWholeNumberInRange(minutes, 5, 720) || minutes % 5 !== 0) {
      errors.push('durationMinutes must be 5-720 minutes, in steps of 5.');
    } else {
      settings.timeAtLocationMinutes = minutes;
      row('Visit length', minutesLabel(s.timeAtLocationMinutes), formatDurationMinutes(minutes));
    }
  }
  if (input.bufferMinutes !== undefined) {
    const minutes = input.bufferMinutes;
    if (!isWholeNumberInRange(minutes, 0, 240) || minutes % 5 !== 0) {
      errors.push('bufferMinutes must be 0-240 minutes, in steps of 5.');
    } else {
      settings.defaultPostAppointmentBufferMinutes = minutes;
      row('Buffer after each visit', minutesLabel(s.defaultPostAppointmentBufferMinutes), minutesLabel(minutes));
    }
  }
  let days: System_DayOfWeek[] | undefined;
  if (input.bookableDays !== undefined) {
    const parsed = Array.isArray(input.bookableDays) ? input.bookableDays.map(parseWeekday) : [null];
    if (parsed.length === 0 || parsed.some(day => day === null)) {
      errors.push('bookableDays must list weekdays, e.g. ["Monday", "Tuesday"].');
    } else {
      days = WEEK_DAYS.filter(day => parsed.includes(day));
      for (const day of WEEK_DAYS) settings[FORM_DAY_FIELDS[day]] = days.includes(day);
      row('Bookable days', formatDayList(formBookableDays(s)), formatDayList(days));
    }
  }
  const earliest = time('earliestStart');
  if (earliest) settings.firstAppointmentTime = earliest;
  const latest = time('latestStart');
  if (latest) settings.lastAppointmentTime = latest;

  const sameDay = flag('sameDayBooking');
  if (sameDay !== undefined) {
    detail.allowSameDayBooking = sameDay;
    row('Same-day booking', onOff(form.allowSameDayBooking ?? true), onOff(sameDay));
  }
  const cutoff = time('sameDayCutoff');
  if (cutoff) {
    settings.sameDayBookingCutoffTime = cutoff;
    row('Same-day cutoff', clockOrUnset(s.sameDayBookingCutoffTime), formatTimeString(cutoff));
  }
  if (input.minimumNoticeHours !== undefined) {
    if (!isWholeNumberInRange(input.minimumNoticeHours, 0, 168)) errors.push('minimumNoticeHours must be a whole number from 0 to 168.');
    else {
      settings.minimumNoticeHours = input.minimumNoticeHours;
      row('Minimum notice', `${s.minimumNoticeHours ?? 0} hours`, `${input.minimumNoticeHours} hours`);
    }
  }
  if (input.maxDaysAhead !== undefined) {
    if (!isWholeNumberInRange(input.maxDaysAhead, 1, 365)) errors.push('maxDaysAhead must be a whole number from 1 to 365.');
    else {
      settings.maxSchedulingDaysFromToday = input.maxDaysAhead;
      row('Books up to', s.maxSchedulingDaysFromToday ? `${s.maxSchedulingDaysFromToday} days out` : 'Not set', `${input.maxDaysAhead} days out`);
    }
  }
  if (input.bookingMode !== undefined) {
    if (input.bookingMode !== 'instant' && input.bookingMode !== 'request') errors.push('bookingMode must be instant or request.');
    else {
      detail.allowInstantBooking = input.bookingMode === 'instant';
      row('Booking', bookingModeLabel(form.allowInstantBooking), bookingModeLabel(detail.allowInstantBooking));
    }
  }
  if (input.assignment !== undefined) {
    if (input.assignment !== 'solo' && input.assignment !== 'round_robin') errors.push('assignment must be solo or round_robin.');
    else {
      detail.eventNature = input.assignment === 'round_robin'
        ? Routemize_Constants_EventNature.ROUND_ROBIN
        : Routemize_Constants_EventNature.SOLO;
      row('Assignment', assignmentLabel(form.eventNature), assignmentLabel(detail.eventNature));
    }
  }
  const messageFlags = [
    ['emailConfirmation', 'emailConfirmation', 'Email confirmation'],
    ['textConfirmation', 'smsConfirmation', 'Text confirmation'],
    ['emailReminders', 'emailReminder', 'Email reminders'],
    ['textReminders', 'smsReminder', 'Text reminders'],
  ] as const;
  for (const [key, field, label] of messageFlags) {
    const value = flag(key);
    if (value === undefined) continue;
    settings[field] = value;
    row(label, s[field] == null ? 'Not set' : onOff(s[field]!), onOff(value));
  }
  if (input.color !== undefined) {
    const color = matchEventColor(input.color);
    if (!color) errors.push(`color must be one of: ${Object.keys(EVENT_COLORS).join(', ')}.`);
    else {
      settings.eventColor = color;
      row('Calendar color', EVENT_COLORS[s.eventColor || DEFAULT_EVENT_COLOR] ?? s.eventColor ?? '', EVENT_COLORS[color]);
    }
  }
  const showInDirectory = flag('showInDirectory');
  if (showInDirectory !== undefined) {
    detail.showInDirectory = showInDirectory;
    row('Listed in the Routemize directory', onOff(form.showInDirectory ?? false), onOff(showInDirectory));
  }
  if (input.directoryCategory !== undefined) {
    const category = matchServiceCategory(input.directoryCategory);
    if (!category) errors.push('directoryCategory must be a trade like Painting, Roofing, Hvac or Other.');
    else {
      detail.directoryCategory = category;
      row('Directory category', form.directoryCategory ? categoryLabel(form.directoryCategory) : 'Not set', categoryLabel(category));
    }
  }

  const windowTouched = ['earliestStart', 'latestStart', 'durationMinutes', 'bookableDays'].some(key => input[key] !== undefined);
  if (windowTouched && errors.length === 0) {
    const fit = fitBookingWindow({
      first: earliest ?? normalizeClockTime(s.firstAppointmentTime) ?? '09:00',
      last: latest ?? normalizeClockTime(s.lastAppointmentTime) ?? '16:00',
      durationMinutes: settings.timeAtLocationMinutes ?? s.timeAtLocationMinutes ?? 45,
      days: days ?? formBookableDays(s),
      companyWeek,
      firstIsExplicit: earliest !== undefined,
      lastIsExplicit: latest !== undefined,
    });
    errors.push(...fit.errors);
    notes.push(...fit.notes);
    if (fit.first !== normalizeClockTime(s.firstAppointmentTime)) {
      settings.firstAppointmentTime = fit.first;
      row('Earliest start', clockOrUnset(s.firstAppointmentTime), formatTimeString(fit.first));
    }
    if (fit.last !== normalizeClockTime(s.lastAppointmentTime)) {
      settings.lastAppointmentTime = fit.last;
      row('Latest start', clockOrUnset(s.lastAppointmentTime), formatTimeString(fit.last));
    }
  }

  return { changes: { detail, settings, notes }, rows, errors };
}

/**
 * The form update body. The endpoint skips null fields except the lead
 * source, which it always overwrites, so that one is echoed from the GET.
 */
export function buildFormDetailUpdate(
  form: Routemize_DTOs_BookingFormDto,
  changes: Routemize_DTOs_UpdateBookingFormDto,
): Routemize_DTOs_UpdateBookingFormDto {
  return { ...changes, id: form.id, leadSource: changes.leadSource ?? form.leadSource ?? null };
}

/**
 * The full settings body: the stored settings with the changes on top, since
 * the endpoint overwrites some fields (ad tracking, detour cap, chatbot share
 * form) whenever they're missing.
 */
export function buildFormSettingsUpdate(
  formId: string,
  stored: Routemize_DTOs_BookingFormSettingDto | null,
  changes: Routemize_DTOs_UpdateBookingFormSettingDto,
): Routemize_DTOs_UpdateBookingFormSettingDto {
  const body: Routemize_DTOs_UpdateBookingFormSettingDto & { id?: string } = { ...stored, ...changes, bookingFormId: formId };
  delete body.id;
  return body;
}

/** The full assignee list after adding and removing people; the endpoint replaces the list. */
export function nextAssigneeIds(current: readonly string[], add: readonly string[], remove: readonly string[]): string[] {
  const removing = new Set(remove);
  const result = current.filter(id => !removing.has(id));
  for (const id of add) {
    if (!result.includes(id)) result.push(id);
  }
  return result;
}

export interface NewFormSettingsInput {
  serviceLabel: string;
  durationMinutes?: number;
  bufferMinutes?: number;
  bookableDays?: readonly System_DayOfWeek[];
  earliestStart?: string;
  latestStart?: string;
  color?: string;
}

/** Settings for a new form, starting from the same defaults as the form editor. */
export function buildNewFormSettings(
  defaults: Routemize_DTOs_BookingFormSettingDto | null,
  input: NewFormSettingsInput,
): Routemize_DTOs_CreateBookingFormSettingDto {
  const days = input.bookableDays ?? WEEKDAYS;
  const dayFlags = Object.fromEntries(WEEK_DAYS.map(day => [FORM_DAY_FIELDS[day], days.includes(day)])) as Record<FormDayField, boolean>;
  return {
    serviceTypeLabel: input.serviceLabel,
    timeAtLocationMinutes: input.durationMinutes ?? defaults?.timeAtLocationMinutes ?? 45,
    defaultPostAppointmentBufferMinutes: input.bufferMinutes ?? defaults?.defaultPostAppointmentBufferMinutes ?? 0,
    firstAppointmentTime: input.earliestStart ?? '09:00',
    lastAppointmentTime: input.latestStart ?? '16:00',
    ...dayFlags,
    sameDayBookingCutoffTime: '12:00',
    minimumNoticeHours: 2,
    maxSchedulingDaysFromToday: 20,
    ultimateSlotPriority: Routemize_Constants_UltimateSlotPriority.COMPANY,
    customerPreferenceThresholdMinutes: null,
    customerPreferenceMileageConstraint: null,
    showNotReadyOption: true,
    requirePhoneVerification: false,
    emailConfirmation: true,
    smsConfirmation: true,
    showGoogleReviews: false,
    afterBookingAction: Routemize_Constants_AfterBookingAction.DISPLAY_CONFIRMATION_PAGE,
    linkURL: null,
    eventColor: input.color ?? DEFAULT_EVENT_COLOR,
    metaPixelId: null,
    metaCapiToken: null,
    metaTestEventCode: null,
    googleAdsConversionId: null,
    googleAdsConversionLabel: null,
    customTrackingScript: null,
  };
}

const NEW_FORM_DETAIL: Routemize_DTOs_BookingFormDto = {
  allowInstantBooking: true,
  allowSameDayBooking: true,
  eventNature: Routemize_Constants_EventNature.SOLO,
  showInDirectory: false,
};

const BOOKING_WINDOW_KEYS = ['earliestStart', 'latestStart', 'durationMinutes', 'bookableDays'] as const;

export interface NewBookingForm {
  detail: Routemize_DTOs_CreateBookingFormDto;
  settings: Routemize_DTOs_CreateBookingFormSettingDto;
  notes: string[];
}

/**
 * A new form from `create_booking_form` input: the form editor's defaults,
 * validated like `update_booking_form`, with the booking window kept inside
 * company hours even when the person didn't mention it.
 */
export function resolveNewBookingForm(
  defaults: Routemize_DTOs_BookingFormSettingDto | null,
  companyWeek: readonly Routemize_DTOs_CompanyWorkScheduleDTO[],
  input: Record<string, unknown>,
): { form: NewBookingForm; errors: string[] } {
  const base = buildNewFormSettings(defaults, { serviceLabel: '' });
  const resolved = resolveBookingFormChanges(NEW_FORM_DETAIL, base, companyWeek, input);
  const errors = [
    ...(['name', 'serviceLabel', 'internalNote'] as const).filter(key => input[key] === undefined).map(key => `${key} is required.`),
    ...resolved.errors,
  ];
  const settings: Routemize_DTOs_CreateBookingFormSettingDto = { ...base, ...resolved.changes.settings };
  const notes = [...resolved.changes.notes];
  if (errors.length === 0 && BOOKING_WINDOW_KEYS.every(key => input[key] === undefined)) {
    const fit = fitBookingWindow({
      first: settings.firstAppointmentTime ?? '09:00',
      last: settings.lastAppointmentTime ?? '16:00',
      durationMinutes: settings.timeAtLocationMinutes ?? 45,
      days: formBookableDays(settings),
      companyWeek,
      firstIsExplicit: false,
      lastIsExplicit: false,
    });
    settings.firstAppointmentTime = fit.first;
    settings.lastAppointmentTime = fit.last;
    errors.push(...fit.errors);
    notes.push(...fit.notes);
  }
  return { form: { detail: { ...NEW_FORM_DETAIL, ...resolved.changes.detail }, settings, notes }, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Form questions
// ─────────────────────────────────────────────────────────────────────────────

type Question = Routemize_DTOs_BookingFormQuestionDto;

export type QuestionKind = 'short_answer' | 'long_answer' | 'dropdown' | 'pick_one' | 'pick_any' | 'photos';

export const QUESTION_KINDS: readonly QuestionKind[] = [
  'short_answer',
  'long_answer',
  'dropdown',
  'pick_one',
  'pick_any',
  'photos',
];

const KIND_UI: Readonly<Record<QuestionKind, QuestionUiType>> = {
  short_answer: 'text',
  long_answer: 'textarea',
  dropdown: 'select',
  pick_one: 'radio',
  pick_any: 'checkbox',
  photos: 'file',
};

/** What the form builder calls each kind. */
export const QUESTION_KIND_LABELS: Readonly<Record<QuestionKind, string>> = {
  short_answer: 'Short answer',
  long_answer: 'Long answer',
  dropdown: 'Dropdown',
  pick_one: 'Pick one',
  pick_any: 'Pick any',
  photos: 'Photos',
};

export function questionKind(question: Pick<Question, 'type' | 'optionsJson'>): QuestionKind {
  const ui = resolveUiType(question.type, question.optionsJson);
  return QUESTION_KINDS.find(kind => KIND_UI[kind] === ui) ?? 'short_answer';
}

function isQuestionKind(value: unknown): value is QuestionKind {
  return typeof value === 'string' && (QUESTION_KINDS as readonly string[]).includes(value);
}

/** Questions are referenced by id, by the `ref` of one added earlier in the same batch, or by exact label. */
export interface AnswerCondition {
  question: string;
  answer: string;
}

export interface AddQuestionEdit {
  action: 'add';
  ref?: string;
  label: string;
  kind: QuestionKind;
  required?: boolean;
  answers?: string[];
  requestOnlyAnswers?: string[];
  after?: string;
  position?: number;
  showWhen?: AnswerCondition;
}

export interface UpdateQuestionEdit {
  action: 'update';
  question: string;
  label?: string;
  kind?: QuestionKind;
  required?: boolean;
  answers?: string[];
  addAnswers?: string[];
  removeAnswers?: string[];
  renameAnswers?: Array<{ from: string; to: string }>;
  requestOnlyAnswers?: string[];
  bookableAnswers?: string[];
}

export type QuestionEdit =
  | AddQuestionEdit
  | UpdateQuestionEdit
  | { action: 'remove'; question: string }
  | { action: 'move'; question: string; after?: string; position?: number }
  | { action: 'show_when'; question: string; showWhen: AnswerCondition }
  | { action: 'always_show'; question: string };

const textList = (value: unknown): string[] | undefined =>
  Array.isArray(value) ? value.map(cleanText).filter(Boolean) : undefined;

function parseCondition(value: unknown): AnswerCondition | null {
  if (!value || typeof value !== 'object') return null;
  const question = cleanText((value as Record<string, unknown>).question);
  const answer = cleanText((value as Record<string, unknown>).answer);
  return question && answer ? { question, answer } : null;
}

/** Validate the raw `edits` list from the model. */
export function parseQuestionEdits(raw: unknown): { edits: QuestionEdit[]; errors: string[] } {
  const edits: QuestionEdit[] = [];
  const errors: string[] = [];
  if (!Array.isArray(raw)) return { edits, errors: ['edits must be a list.'] };

  raw.forEach((item, index) => {
    const where = `Edit ${index + 1}`;
    if (!item || typeof item !== 'object') {
      errors.push(`${where} must be an object.`);
      return;
    }
    const e = item as Record<string, unknown>;
    const question = cleanText(e.question);
    const after = cleanText(e.after) || undefined;
    const position = isWholeNumberInRange(e.position, 1, 500) ? e.position : undefined;
    const showWhen = parseCondition(e.showWhen);
    const required = typeof e.required === 'boolean' ? e.required : undefined;
    if (e.showWhen !== undefined && !showWhen) {
      errors.push(`${where}: showWhen needs a question and one of its answers.`);
      return;
    }
    if (e.kind !== undefined && !isQuestionKind(e.kind)) {
      errors.push(`${where}: kind must be one of ${QUESTION_KINDS.join(', ')}.`);
      return;
    }
    const kind = e.kind as QuestionKind | undefined;

    switch (e.action) {
      case 'add': {
        const label = cleanText(e.label);
        if (!label) errors.push(`${where}: a new question needs a label.`);
        else if (!kind) errors.push(`${where}: a new question needs a kind.`);
        else {
          edits.push({
            action: 'add',
            ref: cleanText(e.ref) || undefined,
            label,
            kind,
            required,
            answers: textList(e.answers),
            requestOnlyAnswers: textList(e.requestOnlyAnswers),
            after,
            position,
            showWhen: showWhen ?? undefined,
          });
        }
        return;
      }
      case 'update': {
        if (!question) {
          errors.push(`${where}: say which question to update.`);
          return;
        }
        const label = e.label === undefined ? undefined : cleanText(e.label);
        if (label === '') {
          errors.push(`${where}: a question's label can't be empty.`);
          return;
        }
        const renameAnswers = Array.isArray(e.renameAnswers)
          ? e.renameAnswers.map(r => ({
            from: cleanText((r as Record<string, unknown>)?.from),
            to: cleanText((r as Record<string, unknown>)?.to),
          }))
          : undefined;
        if (renameAnswers?.some(r => !r.from || !r.to)) {
          errors.push(`${where}: each renameAnswers entry needs from and to.`);
          return;
        }
        const update: UpdateQuestionEdit = {
          action: 'update',
          question,
          label,
          kind,
          required,
          answers: textList(e.answers),
          addAnswers: textList(e.addAnswers),
          removeAnswers: textList(e.removeAnswers),
          renameAnswers,
          requestOnlyAnswers: textList(e.requestOnlyAnswers),
          bookableAnswers: textList(e.bookableAnswers),
        };
        const fields = [
          'label', 'kind', 'required', 'answers', 'addAnswers', 'removeAnswers', 'renameAnswers', 'requestOnlyAnswers', 'bookableAnswers',
        ] as const;
        if (fields.every(field => update[field] === undefined)) {
          errors.push(`${where}: nothing to change on "${question}".`);
          return;
        }
        edits.push(update);
        return;
      }
      case 'remove':
      case 'always_show':
        if (!question) errors.push(`${where}: say which question.`);
        else edits.push({ action: e.action === 'remove' ? 'remove' : 'always_show', question });
        return;
      case 'move':
        if (!question) errors.push(`${where}: say which question to move.`);
        else if (!after && position === undefined) errors.push(`${where}: move needs after or position.`);
        else edits.push({ action: 'move', question, after, position });
        return;
      case 'show_when':
        if (!question || !showWhen) errors.push(`${where}: show_when needs question and showWhen.`);
        else edits.push({ action: 'show_when', question, showWhen });
        return;
      default:
        errors.push(`${where}: action must be add, update, remove, move, show_when or always_show.`);
    }
  });
  return { edits, errors };
}

function sortByOrder<T extends Question>(questions: readonly T[]): T[] {
  return questions
    .map((question, index) => ({ question, index }))
    .sort((a, b) => (a.question.order ?? Number.MAX_SAFE_INTEGER) - (b.question.order ?? Number.MAX_SAFE_INTEGER) || a.index - b.index)
    .map(entry => entry.question);
}

function resolveTarget<T extends Question>(questions: readonly T[], reference: string): T | undefined {
  return questions.find(q => parseFieldOptions(q.optionsJson).branchKey === reference) ?? questions.find(q => q.id === reference);
}

/** The answers that reveal `questionId`, with the question each belongs to. */
function revealers(questions: readonly Question[], questionId: string): Array<{ controller: Question; answer: string }> {
  const target = questions.find(q => q.id === questionId);
  if (!target) return [];
  const branchKey = parseFieldOptions(target.optionsJson).branchKey;
  const result: Array<{ controller: Question; answer: string }> = [];
  for (const controller of questions) {
    const parsed = parseFieldOptions(controller.optionsJson);
    for (const [index, reference] of Object.entries(parsed.optionTargets)) {
      const answer = parsed.options[Number(index)];
      if (answer !== undefined && (reference === branchKey || reference === questionId)) result.push({ controller, answer });
    }
  }
  return result;
}

function leadsTo(questions: readonly Question[], fromId: string, toId: string): boolean {
  const seen = new Set<string>();
  const stack = [fromId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (id === toId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    const question = questions.find(q => q.id === id);
    for (const reference of Object.values(parseFieldOptions(question?.optionsJson).optionTargets)) {
      const target = resolveTarget(questions, reference);
      if (target?.id) stack.push(target.id);
    }
  }
  return false;
}

/**
 * The order the form builder shows and saves: each top-level question, with
 * the follow-ups its answers reveal pulled in right beneath it (by answer
 * order, recursively).
 */
export function orderQuestionsForBuilder<T extends Question>(questions: readonly T[]): T[] {
  const followUps = new Set<T>();
  for (const question of questions) {
    for (const reference of Object.values(parseFieldOptions(question.optionsJson).optionTargets)) {
      const target = resolveTarget(questions, reference);
      if (target && target !== question) followUps.add(target);
    }
  }
  const result: T[] = [];
  const placed = new Set<T>();
  const emit = (question: T) => {
    if (placed.has(question)) return;
    placed.add(question);
    result.push(question);
    const targets = parseFieldOptions(question.optionsJson).optionTargets;
    Object.keys(targets)
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b)
      .forEach(index => {
        const target = resolveTarget(questions, targets[String(index)]);
        if (target) emit(target);
      });
  };
  questions.forEach(question => {
    if (!followUps.has(question)) emit(question);
  });
  questions.forEach(emit);
  return result;
}

const indexOfAnswer = (answers: readonly string[], label: string): number =>
  answers.findIndex(answer => labelKey(answer) === labelKey(label));

function duplicateLabel(labels: readonly string[]): string | null {
  const seen = new Set<string>();
  for (const label of labels) {
    if (seen.has(labelKey(label))) return label;
    seen.add(labelKey(label));
  }
  return null;
}

/** Carry each answer's per-answer settings to its new position; `sources[i]` is the old index, or -1 for a new answer. */
function remapOptions(parsed: ParsedFieldOptions, labels: string[], sources: readonly number[]): FieldOptionsInput {
  const carry = <V,>(map: Record<string, V>): Record<string, V> => {
    const next: Record<string, V> = {};
    sources.forEach((oldIndex, newIndex) => {
      const value = oldIndex >= 0 ? map[String(oldIndex)] : undefined;
      if (value !== undefined) next[String(newIndex)] = value;
    });
    return next;
  };
  return {
    options: labels,
    requestOnlyFlags: sources.map(oldIndex => oldIndex >= 0 && !!parsed.requestOnlyFlags[oldIndex]),
    displayType: parsed.displayType,
    autoAssignedUserIds: carry(parsed.autoAssignedUserIds),
    excludedUserIds: carry(parsed.excludedUserIds),
    durationExtensionMinutes: carry(parsed.durationExtensionMinutes),
    optionTargets: carry(parsed.optionTargets),
    branchKey: parsed.branchKey,
    isDescriptionField: parsed.isDescriptionField,
  };
}

export interface QuestionEditOptions {
  /** Questions to add before the edits run, e.g. from a template. */
  insert?: readonly Question[];
  /** Remove every existing question first. */
  replaceExisting?: boolean;
}

export interface QuestionEditResult {
  /** The whole list after the edits, in builder order with `order` = 1..N. */
  questions: Question[];
  errors: string[];
  warnings: string[];
}

/**
 * Apply a batch of edits to a form's questions, keeping follow-up links,
 * request-only answers and per-answer assignments attached to the right answer.
 * Follows the form builder's rules: only "Pick one" questions reveal follow-ups
 * or carry per-answer assignments, time extensions need a required "Pick one"
 * question, and each answer reveals at most one follow-up.
 */
export function applyQuestionEdits(
  original: readonly Question[],
  edits: readonly QuestionEdit[],
  options: QuestionEditOptions = {},
): QuestionEditResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  let list: Question[] = options.replaceExisting ? [] : orderQuestionsForBuilder(sortByOrder(original)).map(q => ({ ...q }));
  if (options.insert?.length) list = [...list, ...options.insert.map(q => ({ ...q }))];

  const refs = new Map<string, string>();
  const touched = new Set<string>();
  let newCount = 0;

  const find = (id: string) => list.find(q => q.id === id);

  const resolve = (reference: string, where: string): Question | null => {
    const refId = refs.get(reference);
    const byRef = refId ? find(refId) : undefined;
    if (byRef) return byRef;
    const byId = find(reference);
    if (byId) return byId;
    const matches = list.filter(q => labelKey(q.label) === labelKey(reference));
    if (matches.length === 1) return matches[0];
    errors.push(
      matches.length > 1
        ? `${where}: more than one question is labeled "${reference}"; use its id.`
        : `${where}: no question matches "${reference}".`,
    );
    return null;
  };

  const insert = (question: Question, anchor: Question | null, position?: number): void => {
    if (anchor) list.splice(list.indexOf(anchor) + 1, 0, question);
    else if (position !== undefined) list.splice(Math.min(position - 1, list.length), 0, question);
    else list.push(question);
  };

  const warnIfOrphaned = (followUp: Question | undefined) => {
    if (followUp?.id && find(followUp.id) && revealers(list, followUp.id).length === 0) {
      warnings.push(`"${followUp.label}" no longer has an answer that shows it, so every customer will see it.`);
    }
  };

  const link = (target: Question, condition: AnswerCondition, where: string): void => {
    const controller = resolve(condition.question, where);
    if (!controller?.id || !target.id) return;
    if (controller.id === target.id) {
      errors.push(`${where}: a question can't depend on its own answer.`);
      return;
    }
    const kind = questionKind(controller);
    if (kind !== 'pick_one') {
      errors.push(`${where}: only "Pick one" questions can show follow-ups, and "${controller.label}" is ${QUESTION_KIND_LABELS[kind]}.`);
      return;
    }
    const parsed = parseFieldOptions(controller.optionsJson);
    const index = indexOfAnswer(parsed.options, condition.answer);
    if (index < 0) {
      errors.push(`${where}: "${controller.label}" has no answer "${condition.answer}". Its answers: ${parsed.options.join(', ')}.`);
      return;
    }
    const current = parsed.optionTargets[String(index)] ? resolveTarget(list, parsed.optionTargets[String(index)]) : undefined;
    if (current?.id === target.id) return;
    if (current) {
      errors.push(
        `${where}: "${parsed.options[index]}" on "${controller.label}" already shows "${current.label}", and an answer can show ` +
          `only one follow-up. Make "${current.label}" always_show first to swap it.`,
      );
      return;
    }
    if (leadsTo(list, target.id, controller.id)) {
      errors.push(`${where}: "${target.label}" already leads to "${controller.label}", so this would loop.`);
      return;
    }
    list = linkAnswerToQuestion(list, controller.id, index, target.id);
  };

  const applyUpdate = (edit: UpdateQuestionEdit, where: string): void => {
    const target = resolve(edit.question, where);
    if (!target?.id) return;
    const id = target.id;
    const oldKind = questionKind(target);
    const newKind = edit.kind ?? oldKind;
    const newUi = KIND_UI[newKind];
    const required = edit.required ?? !!target.isRequired;
    const answersTouched = [edit.answers, edit.addAnswers, edit.removeAnswers, edit.renameAnswers].some(v => v !== undefined);
    const flagsTouched = edit.requestOnlyAnswers !== undefined || edit.bookableAnswers !== undefined;
    let parsed = parseFieldOptions(target.optionsJson);
    const durationsAtRisk = Object.keys(parsed.durationExtensionMinutes).length > 0 && !required;

    const next: Question = { ...target };
    if (edit.label !== undefined) next.label = edit.label;
    if (edit.required !== undefined) next.isRequired = edit.required;

    if (newKind !== oldKind || answersTouched || flagsTouched || durationsAtRisk) {
      let labels = [...parsed.options];
      let sources = labels.map((_, index) => index);
      if (!isChoiceUiType(newUi)) {
        if (answersTouched || flagsTouched) {
          errors.push(`${where}: ${QUESTION_KIND_LABELS[newKind]} questions don't have answers to pick from.`);
          return;
        }
        labels = [];
        sources = [];
      } else {
        if (edit.answers) {
          const used = new Set<number>();
          sources = edit.answers.map(label => {
            const index = parsed.options.findIndex((existing, i) => !used.has(i) && labelKey(existing) === labelKey(label));
            if (index >= 0) used.add(index);
            return index;
          });
          labels = [...edit.answers];
        }
        for (const rename of edit.renameAnswers ?? []) {
          const index = indexOfAnswer(labels, rename.from);
          if (index < 0) {
            errors.push(`${where}: "${next.label}" has no answer "${rename.from}" to rename.`);
            return;
          }
          labels[index] = rename.to;
        }
        for (const label of edit.removeAnswers ?? []) {
          const index = indexOfAnswer(labels, label);
          if (index < 0) {
            errors.push(`${where}: "${next.label}" has no answer "${label}" to remove.`);
            return;
          }
          labels.splice(index, 1);
          sources.splice(index, 1);
        }
        for (const label of edit.addAnswers ?? []) {
          labels.push(label);
          sources.push(-1);
        }
        const duplicate = duplicateLabel(labels);
        if (duplicate) {
          errors.push(`${where}: "${duplicate}" is listed twice.`);
          return;
        }
        if (labels.length === 0) {
          errors.push(`${where}: "${next.label}" needs at least one answer.`);
          return;
        }
      }

      const kept = new Set(newUi === 'radio' ? sources.filter(index => index >= 0) : []);
      for (const [key, reference] of Object.entries(parsed.optionTargets)) {
        if (kept.has(Number(key))) continue;
        const followUp = resolveTarget(list, reference);
        list = unlinkAnswer(list, id, Number(key));
        warnIfOrphaned(followUp);
      }
      parsed = parseFieldOptions(find(id)?.optionsJson);

      const remapped = remapOptions(parsed, labels, sources);
      const flags = remapped.requestOnlyFlags ?? [];
      for (const [answers, value] of [[edit.requestOnlyAnswers, true], [edit.bookableAnswers, false]] as const) {
        for (const label of answers ?? []) {
          const index = indexOfAnswer(labels, label);
          if (index < 0) {
            errors.push(`${where}: "${next.label}" has no answer "${label}".`);
            return;
          }
          flags[index] = value;
        }
      }
      remapped.requestOnlyFlags = flags;

      const hasAny = (map: Record<string, unknown> | undefined) => Object.keys(map ?? {}).length > 0;
      if (newUi !== 'radio') {
        if (hasAny(remapped.autoAssignedUserIds) || hasAny(remapped.durationExtensionMinutes)) {
          warnings.push(`"${next.label}" lost its per-answer assignments and time extensions, which only work on "Pick one" questions.`);
        }
        remapped.autoAssignedUserIds = {};
        remapped.durationExtensionMinutes = {};
        remapped.optionTargets = {};
      } else if (!required && hasAny(remapped.durationExtensionMinutes)) {
        warnings.push(`"${next.label}" lost its time extensions, which only apply to required questions.`);
        remapped.durationExtensionMinutes = {};
      }
      if (!isChoiceUiType(newUi) && hasAny(remapped.excludedUserIds)) {
        warnings.push(`"${next.label}" lost its per-answer team exclusions.`);
        remapped.excludedUserIds = {};
      }
      remapped.displayType = newUi === 'radio' ? 'radio' : undefined;

      next.type = UI_TO_FIELD_TYPE[newUi] as Routemize_Constants_FieldType;
      next.optionsJson =
        isChoiceUiType(newUi) || remapped.branchKey || remapped.isDescriptionField || target.optionsJson
          ? stringifyFieldOptions(remapped)
          : null;
    }
    list = list.map(q => (q.id === id ? next : q));
    touched.add(id);
  };

  edits.forEach((edit, index) => {
    const where = `Edit ${index + 1}`;
    switch (edit.action) {
      case 'add': {
        const ui = KIND_UI[edit.kind];
        const answers = edit.answers ?? [];
        if (!isChoiceUiType(ui) && (answers.length > 0 || edit.requestOnlyAnswers?.length)) {
          errors.push(`${where}: ${QUESTION_KIND_LABELS[edit.kind]} questions don't have answers to pick from.`);
          return;
        }
        if (isChoiceUiType(ui) && answers.length === 0) {
          errors.push(`${where}: "${edit.label}" needs at least one answer.`);
          return;
        }
        const duplicate = duplicateLabel(answers);
        if (duplicate) {
          errors.push(`${where}: "${duplicate}" is listed twice.`);
          return;
        }
        const flags = answers.map(() => false);
        for (const label of edit.requestOnlyAnswers ?? []) {
          const answerIndex = indexOfAnswer(answers, label);
          if (answerIndex < 0) {
            errors.push(`${where}: "${label}" isn't one of the answers.`);
            return;
          }
          flags[answerIndex] = true;
        }
        if (edit.ref && refs.has(edit.ref)) {
          errors.push(`${where}: ref "${edit.ref}" is already used in this batch.`);
          return;
        }
        const anchor = edit.after ? resolve(edit.after, where) : null;
        if (edit.after && !anchor) return;
        newCount += 1;
        const question: Question = {
          id: `new_${newCount}`,
          label: edit.label,
          type: UI_TO_FIELD_TYPE[ui] as Routemize_Constants_FieldType,
          isRequired: edit.required ?? false,
          order: null,
          optionsJson: isChoiceUiType(ui)
            ? stringifyFieldOptions({ options: answers, requestOnlyFlags: flags, displayType: ui === 'radio' ? 'radio' : undefined })
            : null,
        };
        insert(question, anchor, edit.position);
        if (edit.ref) refs.set(edit.ref, question.id!);
        touched.add(question.id!);
        if (edit.showWhen) link(question, edit.showWhen, where);
        return;
      }
      case 'update':
        applyUpdate(edit, where);
        return;
      case 'remove': {
        const target = resolve(edit.question, where);
        if (!target?.id) return;
        const followUps = Object.values(parseFieldOptions(target.optionsJson).optionTargets).map(ref => resolveTarget(list, ref));
        list = removeQuestion(list, target.id);
        followUps.forEach(warnIfOrphaned);
        return;
      }
      case 'move': {
        const target = resolve(edit.question, where);
        if (!target?.id) return;
        const anchor = edit.after ? resolve(edit.after, where) : null;
        if (edit.after && !anchor) return;
        if (anchor?.id === target.id) {
          errors.push(`${where}: a question can't move after itself.`);
          return;
        }
        list = list.filter(q => q.id !== target.id);
        insert(target, anchor, edit.position);
        if (revealers(list, target.id).length > 0) {
          warnings.push(`"${target.label}" is a follow-up, so it stays right under the question that shows it.`);
        }
        return;
      }
      case 'show_when': {
        const target = resolve(edit.question, where);
        if (target) link(target, edit.showWhen, where);
        return;
      }
      case 'always_show': {
        const target = resolve(edit.question, where);
        if (target?.id) list = detachQuestion(list, target.id);
        return;
      }
    }
  });

  for (const question of list) {
    if (!question.id || !touched.has(question.id)) continue;
    if (isChoiceUiType(KIND_UI[questionKind(question)]) && parseFieldOptions(question.optionsJson).options.length === 0) {
      errors.push(`"${question.label}" needs at least one answer.`);
    }
  }

  const questions = orderQuestionsForBuilder(list).map((question, index) => ({ ...question, order: index + 1 }));
  return { questions, errors, warnings };
}

/** No answers, follow-up key or description flag: the builder saves these as null. */
function hasOptionsPayload(optionsJson: string | null | undefined): boolean {
  if (!optionsJson) return false;
  const parsed = parseFieldOptions(optionsJson);
  return parsed.options.length > 0 || !!parsed.branchKey || parsed.isDescriptionField || Object.keys(parsed.optionTargets).length > 0;
}

function sameOptionsJson(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!hasOptionsPayload(a) && !hasOptionsPayload(b)) return true;
  return (a ?? null) === (b ?? null);
}

export interface QuestionWrites {
  creates: Routemize_DTOs_CreateFormQuestionDto[];
  updates: Routemize_DTOs_UpdateFormQuestionDto[];
  deleteIds: string[];
}

/** The API calls that turn `original` into `next`. Questions whose id isn't in `original` are created. */
export function diffQuestions(original: readonly Question[], next: readonly Question[], bookingFormId: string): QuestionWrites {
  const before = new Map(original.filter(q => q.id).map(q => [q.id!, q]));
  const creates: Routemize_DTOs_CreateFormQuestionDto[] = [];
  const updates: Routemize_DTOs_UpdateFormQuestionDto[] = [];
  for (const question of next) {
    const previous = question.id ? before.get(question.id) : undefined;
    if (!previous) {
      creates.push({
        bookingFormId,
        label: question.label,
        type: question.type,
        isRequired: !!question.isRequired,
        order: question.order,
        optionsJson: hasOptionsPayload(question.optionsJson) ? question.optionsJson : null,
      });
      continue;
    }
    const changed =
      previous.label !== question.label ||
      previous.type !== question.type ||
      !!previous.isRequired !== !!question.isRequired ||
      previous.order !== question.order ||
      !sameOptionsJson(previous.optionsJson, question.optionsJson);
    if (changed) {
      updates.push({
        id: question.id,
        bookingFormId,
        label: question.label,
        type: question.type,
        isRequired: !!question.isRequired,
        order: question.order,
        optionsJson: question.optionsJson ?? null,
      });
    }
  }
  const kept = new Set(next.map(q => q.id));
  const deleteIds = [...before.keys()].filter(id => !kept.has(id));
  return { creates, updates, deleteIds };
}

function questionShape(question: Question): string {
  const kind = questionKind(question);
  const parsed = parseFieldOptions(question.optionsJson);
  const answers = isChoiceUiType(KIND_UI[kind])
    ? parsed.options.map((answer, i) => (parsed.requestOnlyFlags[i] ? `${answer} (request)` : answer)).join(' · ')
    : '';
  const head = `${QUESTION_KIND_LABELS[kind]}${question.isRequired ? ', required' : ''}`;
  return answers ? `${head}: ${answers}` : head;
}

function contentKey(questions: readonly Question[], question: Question): string {
  const parsed = parseFieldOptions(question.optionsJson);
  return JSON.stringify([
    question.label,
    questionKind(question),
    !!question.isRequired,
    parsed.options,
    parsed.requestOnlyFlags,
    Object.keys(parsed.optionTargets).sort(),
    revealers(questions, question.id ?? '').map(r => `${r.controller.id}:${r.answer}`).sort(),
  ]);
}

/** The questions after an edit as an indented outline, follow-ups under the answer that shows them. */
export function buildQuestionOutline(original: readonly Question[], next: readonly Question[]): ConfirmOutlineRow[] {
  const before = new Map(original.filter(q => q.id).map(q => [q.id!, q]));
  const depth = new Map<string, number>();
  const rows: ConfirmOutlineRow[] = next.map(question => {
    const shownBy = revealers(next, question.id ?? '');
    const parentDepth = shownBy.map(r => depth.get(r.controller.id ?? '')).find((d): d is number => d !== undefined);
    const level = shownBy.length > 0 ? Math.min((parentDepth ?? 0) + 1, 4) : 0;
    depth.set(question.id ?? '', level);
    const previous = question.id ? before.get(question.id) : undefined;
    const tag = !previous ? 'New' : contentKey(original, previous) !== contentKey(next, question) ? 'Edited' : undefined;
    const condition = shownBy.length > 0 ? `If ${shownBy.map(r => `"${r.answer}"`).join(' or ')}: ` : '';
    return { text: `${condition}${question.label ?? ''}`, depth: level, tag, detail: questionShape(question) };
  });
  const kept = new Set(next.map(q => q.id));
  for (const question of sortByOrder(original)) {
    if (question.id && !kept.has(question.id)) rows.push({ text: question.label ?? '', depth: 0, tag: 'Removed' });
  }
  return rows;
}

export interface QuestionSummary {
  id: string;
  label: string;
  kind: QuestionKind;
  required: boolean;
  answers?: Array<{
    label: string;
    requestOnly?: true;
    showsQuestionId?: string;
    assignsUserId?: string;
    excludesUserIds?: string[];
    addsMinutes?: number;
  }>;
  shownWhen?: Array<{ questionId: string; answer: string }>;
  descriptionField?: true;
}

/** A form's questions in builder order, with their follow-up wiring spelled out for the model. */
export function summarizeQuestions(questions: readonly Question[]): QuestionSummary[] {
  const ordered = orderQuestionsForBuilder(sortByOrder(questions));
  return ordered.map(question => {
    const kind = questionKind(question);
    const parsed = parseFieldOptions(question.optionsJson);
    const summary: QuestionSummary = {
      id: question.id ?? '',
      label: question.label ?? '',
      kind,
      required: !!question.isRequired,
    };
    if (isChoiceUiType(KIND_UI[kind])) {
      summary.answers = parsed.options.map((label, index) => {
        const key = String(index);
        const target = parsed.optionTargets[key] ? resolveTarget(ordered, parsed.optionTargets[key]) : undefined;
        return {
          label,
          ...(parsed.requestOnlyFlags[index] ? { requestOnly: true as const } : {}),
          ...(target?.id ? { showsQuestionId: target.id } : {}),
          ...(parsed.autoAssignedUserIds[key] ? { assignsUserId: parsed.autoAssignedUserIds[key] } : {}),
          ...(parsed.excludedUserIds[key]?.length ? { excludesUserIds: parsed.excludedUserIds[key] } : {}),
          ...(parsed.durationExtensionMinutes[key] ? { addsMinutes: parsed.durationExtensionMinutes[key] } : {}),
        };
      });
    }
    const shownBy = revealers(ordered, question.id ?? '');
    if (shownBy.length > 0) summary.shownWhen = shownBy.map(r => ({ questionId: r.controller.id ?? '', answer: r.answer }));
    if (parsed.isDescriptionField) summary.descriptionField = true;
    return summary;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Question templates
// ─────────────────────────────────────────────────────────────────────────────

function findIndustry(value: string): IndustryTemplate | undefined {
  const key = labelKey(value);
  return INDUSTRY_TEMPLATES.find(industry => industry.id === key || labelKey(industry.name) === key);
}

const wizardDefaultAddOns = (industryId: string, goal: FormGoal): string[] =>
  getAddOnsForIndustry(industryId)
    .filter(addOn => addOn.defaultEnabled || (addOn.qualifiedOnly && goal === 'qualified'))
    .map(addOn => addOn.id);

/** Without an industry, the list of industries; with one, its services and add-on questions. */
export function describeFormTemplates(industry?: string): Record<string, unknown> {
  if (!industry) {
    return {
      industries: INDUSTRY_TEMPLATES.map(t => ({ id: t.id, name: t.name, covers: t.tagline })),
    };
  }
  const template = findIndustry(industry);
  if (!template) {
    return { error: `No template for "${industry}".`, industries: INDUSTRY_TEMPLATES.map(t => t.id) };
  }
  return {
    id: template.id,
    name: template.name,
    projectQuestion: template.projectQuestion.label,
    services: template.projectQuestion.services.map(service => ({
      id: service.id,
      label: service.label,
      ...(service.followUp ? { followUp: service.followUp.label } : {}),
      ...(service.lowIntent ? { lowIntent: true } : {}),
    })),
    addOns: getAddOnsForIndustry(template.id).map(addOn => ({
      id: addOn.id,
      label: addOn.label,
      description: addOn.description,
      onByDefaultFor: addOn.defaultEnabled ? 'both' : addOn.qualifiedOnly ? 'qualified' : 'neither',
    })),
  };
}

export interface TemplateRequest {
  industry: string;
  services: string[];
  goal: FormGoal;
  /** Omitted: the same add-ons the form builder's wizard turns on by default. */
  addOns?: string[];
  skipFollowUpsFor?: string[];
  includePhotoPrompts?: boolean;
}

/** Validate the raw `template` input; the goal defaults to qualified estimates. */
export function parseTemplateRequest(raw: unknown): { request: TemplateRequest | null; errors: string[] } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { request: null, errors: ['template must be an object with industry and services.'] };
  }
  const entry = raw as Record<string, unknown>;
  const errors: string[] = [];
  const industry = cleanText(entry.industry);
  const services = textList(entry.services) ?? [];
  if (!industry) errors.push('template.industry is required (see list_form_templates).');
  if (services.length === 0) errors.push('template.services must list at least one service.');
  if (entry.goal !== undefined && entry.goal !== 'bookings' && entry.goal !== 'qualified') {
    errors.push('template.goal must be "bookings" or "qualified".');
  }
  if (entry.includePhotoPrompts !== undefined && typeof entry.includePhotoPrompts !== 'boolean') {
    errors.push('template.includePhotoPrompts must be true or false.');
  }
  if (errors.length > 0) return { request: null, errors };
  return {
    request: {
      industry,
      services,
      goal: entry.goal === 'bookings' ? 'bookings' : 'qualified',
      addOns: textList(entry.addOns),
      skipFollowUpsFor: textList(entry.skipFollowUpsFor),
      includePhotoPrompts: entry.includePhotoPrompts as boolean | undefined,
    },
    errors: [],
  };
}

/** Generate a template's questions the same way the form builder's AI wizard does. */
export function buildTemplateQuestions(
  request: TemplateRequest,
  bookingFormId: string | null,
): { questions: Question[]; errors: string[] } {
  const template = findIndustry(request.industry);
  if (!template) {
    return { questions: [], errors: [`No template for "${request.industry}". Use list_form_templates for the options.`] };
  }
  const services = template.projectQuestion.services;
  const findService = (value: string) =>
    services.find(service => service.id === labelKey(value) || labelKey(service.label) === labelKey(value));
  const errors: string[] = [];
  const pickServices = (values: readonly string[]): string[] =>
    values.flatMap(value => {
      const service = findService(value);
      if (!service) errors.push(`${template.name} has no service "${value}". Options: ${services.map(s => s.id).join(', ')}.`);
      return service ? [service.id] : [];
    });

  const serviceIds = pickServices(request.services);
  if (request.services.length === 0) errors.push('Pick at least one service the business offers.');
  const skipped = pickServices(request.skipFollowUpsFor ?? []);
  const addOnOptions = getAddOnsForIndustry(template.id);
  const addOnIds = request.addOns
    ? request.addOns.flatMap(value => {
      const addOn = addOnOptions.find(a => a.id === labelKey(value) || labelKey(a.label) === labelKey(value));
      if (!addOn) errors.push(`No add-on "${value}". Options: ${addOnOptions.map(a => a.id).join(', ')}.`);
      return addOn ? [addOn.id] : [];
    })
    : wizardDefaultAddOns(template.id, request.goal);
  if (errors.length > 0) return { questions: [], errors };

  const questions = generateFormQuestions({
    industryId: template.id,
    selectedServiceIds: serviceIds,
    goal: request.goal,
    disabledFollowUpServiceIds: skipped,
    includePhotoPrompts: request.includePhotoPrompts,
    enabledAddOnIds: addOnIds,
    bookingFormId,
  });
  return { questions, errors: [] };
}

/**
 * A template's questions without follow-ups or request-only answers, for plans
 * without form pre-qualification. Follow-ups are dropped rather than shown to
 * everyone, since most only make sense after their answer.
 */
export function flattenTemplateQuestions(questions: readonly Question[]): Question[] {
  return questions
    .filter(question => !parseFieldOptions(question.optionsJson).branchKey)
    .map((question, index) => {
      const flat = stringifyFieldOptions({ ...parseFieldOptions(question.optionsJson), requestOnlyFlags: [], optionTargets: {} });
      return { ...question, order: index + 1, optionsJson: hasOptionsPayload(flat) ? flat : null };
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Team
// ─────────────────────────────────────────────────────────────────────────────

export function matchRoleName(value: unknown): SystemRoleName | null {
  const key = labelKey(typeof value === 'string' ? value : '');
  return SYSTEM_ROLE_NAMES.find(name => labelKey(name) === key) ?? null;
}

const ROLE_RANK: Readonly<Record<SystemRoleName, number>> = {
  'Super User': 3,
  'Scheduling Assistant': 2,
  'Team Member': 1,
};

/** The highest system role among someone's roles. */
export function primaryRoleName(roleNames: readonly (string | null | undefined)[] | null | undefined): SystemRoleName | null {
  const roles = (roleNames ?? []).map(matchRoleName).filter((role): role is SystemRoleName => role !== null);
  return roles.sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a])[0] ?? null;
}

/** Moving to a role with less access than the current one. */
export function isRoleDowngrade(from: SystemRoleName | null, to: SystemRoleName): boolean {
  return from !== null && ROLE_RANK[to] < ROLE_RANK[from];
}

interface MatchableMember {
  id?: string;
  name?: string | null;
  email?: string | null;
  isActive?: boolean;
}

/**
 * The one team member someone means: by id or email, else an active member by
 * full name, else by first name or name prefix. Several matches is an error
 * that names them.
 */
export function matchTeamMember<T extends MatchableMember>(
  members: readonly T[],
  value: string,
): { member: T } | { error: string } {
  const key = labelKey(value);
  if (!key) return { error: 'Say which team member.' };
  const direct = members.find(m => m.id === value.trim()) ?? members.find(m => labelKey(m.email) === key);
  if (direct) return { member: direct };
  const active = members.filter(m => m.isActive !== false);
  const byName = active.filter(m => labelKey(m.name) === key);
  const candidates = byName.length > 0
    ? byName
    : active.filter(m => labelKey(m.name).split(' ').includes(key) || labelKey(m.name).startsWith(key));
  if (candidates.length === 1) return { member: candidates[0] };
  if (candidates.length === 0) return { error: `No active team member matches "${value}".` };
  return { error: `"${value}" matches ${candidates.map(m => m.name || m.email).join(', ')}. Say which one.` };
}

export interface InviteDraft {
  email: string;
  firstName: string;
  lastName: string;
  roleName: SystemRoleName;
}

/** Validate the raw `people` list from the model. */
export function parseInvites(raw: unknown): { invites: InviteDraft[]; errors: string[] } {
  const invites: InviteDraft[] = [];
  const errors: string[] = [];
  if (!Array.isArray(raw) || raw.length === 0) return { invites, errors: ['people must list at least one person.'] };
  raw.forEach((item, index) => {
    const entry = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const email = cleanText(entry.email).toLowerCase();
    const firstName = cleanText(entry.firstName);
    const lastName = cleanText(entry.lastName);
    const roleName = matchRoleName(entry.role);
    const who = firstName || email || `Person ${index + 1}`;
    if (!isValidEmail(email)) errors.push(`${who}: needs a valid email address.`);
    else if (invites.some(invite => invite.email === email)) errors.push(`${email} is listed twice.`);
    else if (!firstName || !isValidName(firstName) || firstName.length > MAX_NAME_LENGTH) errors.push(`${who}: needs a first name (letters only).`);
    else if (lastName && (!isValidName(lastName) || lastName.length > MAX_NAME_LENGTH)) errors.push(`${who}: the last name can only use letters.`);
    else if (!roleName) errors.push(`${who}: role must be one of ${SYSTEM_ROLE_NAMES.join(', ')}.`);
    else invites.push({ email, firstName, lastName, roleName });
  });
  return { invites, errors };
}

/**
 * Which invites the Starter plan's role rule allows (one non-Team-Member user
 * besides the owner, plus paid add-on seats), counting earlier invites in the
 * same batch as filled.
 */
export function starterRoleAllowance(params: {
  tier: PlanTier | null;
  additionalSeats: number;
  members: readonly StarterSeatMember[];
  invites: readonly InviteDraft[];
}): boolean[] {
  const members = [...params.members];
  return params.invites.map(invite => {
    const allowed = isRoleAllowedOnPlan({
      tier: params.tier,
      additionalSeats: params.additionalSeats,
      members,
      roleName: invite.roleName,
    });
    if (allowed) members.push({ id: null, roleNames: [invite.roleName] });
    return allowed;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Weekly availability
// ─────────────────────────────────────────────────────────────────────────────

export interface WeeklyHoursChange {
  day: System_DayOfWeek;
  start?: string;
  end?: string;
  /** Drop the person's own hours for the day so company hours apply again. */
  useCompanyHours?: boolean;
}

export interface WeeklyHoursStep {
  day: EffectiveScheduleDay;
  kind: 'set' | 'revert';
  start?: string;
  end?: string;
}

const scheduleDayLabel = (isWorkDay: boolean, start: string, end: string, suffix = ''): string =>
  isWorkDay ? `${formatTimeString(start)} – ${formatTimeString(end)}${suffix}` : 'Off';

const effectiveDayLabel = (day: EffectiveScheduleDay): string =>
  scheduleDayLabel(day.effectiveIsWorkDay, day.effectiveStartTime, day.effectiveEndTime, day.isOverridden ? '' : ' (company hours)');

/**
 * The writes that give someone their own hours on a weekday, or hand the day
 * back to company hours, mirroring the work schedule modal: own hours only on
 * days the company is open.
 */
export function planWeeklyAvailability(
  schedule: readonly EffectiveScheduleDay[],
  changes: readonly WeeklyHoursChange[],
): { steps: WeeklyHoursStep[]; rows: ConfirmChange[]; errors: string[] } {
  const steps: WeeklyHoursStep[] = [];
  const rows: ConfirmChange[] = [];
  const errors: string[] = [];
  const seen = new Set<System_DayOfWeek>();
  for (const change of changes) {
    const day = schedule.find(d => d.dayOfWeek === change.day);
    if (seen.has(change.day)) {
      errors.push(`${change.day} is listed twice.`);
      continue;
    }
    seen.add(change.day);
    if (!day) {
      errors.push(`No schedule found for ${change.day}.`);
      continue;
    }
    const hasHours = change.start !== undefined || change.end !== undefined;
    if (change.useCompanyHours) {
      if (hasHours) errors.push(`${day.dayLabel}: give custom hours or company hours, not both.`);
      else if (day.isOverridden) {
        steps.push({ day, kind: 'revert' });
        rows.push({
          label: day.dayLabel,
          from: effectiveDayLabel(day),
          to: scheduleDayLabel(day.companyIsWorkDay, day.companyStartTime, day.companyEndTime, ' (company hours)'),
        });
      }
      continue;
    }
    const start = normalizeClockTime(change.start);
    const end = normalizeClockTime(change.end);
    if (!start || !end) {
      errors.push(`${day.dayLabel}: give start and end as 24-hour times like "08:00", or useCompanyHours.`);
      continue;
    }
    if (timeStringToMinutes(start)! >= timeStringToMinutes(end)!) {
      errors.push(`${day.dayLabel}: the end has to be after the start.`);
      continue;
    }
    if (!day.companyIsWorkDay) {
      errors.push(`${day.dayLabel}s are closed in company hours, so the company hours have to open that day first.`);
      continue;
    }
    const sameAsNow = day.effectiveIsWorkDay
      && normalizeClockTime(day.effectiveStartTime) === start
      && normalizeClockTime(day.effectiveEndTime) === end;
    if (sameAsNow) continue;
    steps.push({ day, kind: 'set', start, end });
    rows.push({ label: day.dayLabel, from: effectiveDayLabel(day), to: scheduleDayLabel(true, start, end) });
  }
  return { steps, rows, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Addresses
// ─────────────────────────────────────────────────────────────────────────────

export interface AddressParts {
  addressLine1: string;
  addressLine2?: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
}

/**
 * The single address to upsert so `home` becomes the address routes start and
 * end at: the Home entry route planning already resolves to (active first), or
 * a new active Home entry.
 */
export function homeAddressUpsert(
  addresses: readonly Routemize_DTOs_UserAddressDto[],
  home: AddressParts,
): Routemize_DTOs_UserAddressDto {
  const isHome = (address: Routemize_DTOs_UserAddressDto) => address.addressType === Routemize_Constants_AddressType.HOME;
  const existing = addresses.find(a => isHome(a) && a.isActive !== false) ?? addresses.find(isHome);
  return {
    ...(existing?.id ? { id: existing.id } : {}),
    addressType: Routemize_Constants_AddressType.HOME,
    addressLine1: home.addressLine1,
    addressLine2: home.addressLine2 ?? null,
    city: home.city,
    state: home.state,
    zipCode: home.zipCode,
    country: home.country,
    isActive: true,
  };
}
